import { useId, type ReactNode } from "react";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Applied when this option is the selected one — the cost/revenue hue key. */
  accent?: string;
  hint?: string;
}

interface SegmentedProps<T extends string> {
  legend: ReactNode;
  /** Visually hide the legend when the surrounding card already says it. */
  hideLegend?: boolean;
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  size?: "sm" | "md";
}

/**
 * A segmented control that is really a radio group.
 *
 * Built from `<input type="radio">` rather than buttons with `aria-checked`
 * because the browser then gives us the whole behaviour for free: arrow keys
 * move between options, only the selected one is a tab stop, and a screen reader
 * announces "2 of 3". Every hand-rolled version of this has to reimplement that,
 * and most reimplement only half.
 */
export function Segmented<T extends string>({
  legend,
  hideLegend = false,
  value,
  options,
  onChange,
  disabled = false,
  size = "md",
}: SegmentedProps<T>) {
  const name = useId();

  return (
    <fieldset disabled={disabled} className="min-w-0">
      <legend className={hideLegend ? "sr-only" : "mb-1 text-xs font-semibold text-ink"}>
        {legend}
      </legend>
      <div className="inline-flex flex-wrap rounded-lg border border-line bg-chip-bg p-0.5">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <label
              key={option.value}
              title={option.hint}
              className={[
                "cursor-pointer rounded-md font-semibold transition",
                size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
                // `text-on-accent`, not `text-white`: the selected chip is filled
                // with --azure-deep or a ledger hue, and all of those are light
                // colours in the dark theme.
                selected ? "text-on-accent" : "text-ink-soft hover:text-ink",
                disabled ? "cursor-not-allowed opacity-60" : "",
                // The focus ring belongs on the label, since the input itself is
                // off screen; `:has` puts it there when the input inside is focused.
                "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[var(--azure-deep)]",
              ]
                .filter(Boolean)
                .join(" ")}
              style={selected ? { background: option.accent ?? "var(--azure-deep)" } : undefined}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
